import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, errorMessage } from '../lib/api.ts';
import { initials } from '../lib/format.ts';
import { focusFirst, getInput } from '../lib/nav/engine.ts';
import { audio } from '../lib/audio/engine.ts';
import { Icon } from './Icon.tsx';

// ---------- Toasts ----------
type Toast = { id: number; text: string; tone: 'info' | 'ok' | 'error' };
const ToastCtx = createContext<(text: string, tone?: Toast['tone']) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Toast['tone'] = 'info') => {
    const id = Date.now() + Math.random();
    audio.play(tone === 'error' ? 'error' : tone === 'ok' ? 'success' : 'notify');
    setToasts((t) => [...t.slice(-3), { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === 'error' ? 7000 : 4000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

// ---------- States ----------
export function Loading({ label = 'Cargando' }: { label?: string }) {
  return (
    <div className="loading-page" role="status">
      <div className="spinner" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const retryable = error instanceof ApiError ? error.retryable : true;
  return (
    <div className="empty" role="alert">
      <h2>
        {error instanceof ApiError && error.kind === 'network'
          ? 'Sin conexión'
          : error instanceof ApiError && error.kind === 'not_found'
            ? 'No encontrado'
            : 'No se pudo cargar'}
      </h2>
      <p>{errorMessage(error)}</p>
      {onRetry && retryable && (
        <button className="btn" onClick={onRetry} data-autofocus>
          <Icon name="refresh" /> Reintentar
        </button>
      )}
    </div>
  );
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <h2>{title}</h2>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/** Restore focus to whatever opened a layer, and move focus into it when navigating without a mouse. */
export function useLayerFocus(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    audio.play('menuOpen');
    const prev = document.activeElement as HTMLElement | null;
    const t = setTimeout(() => {
      if (!ref.current) return;
      const first = ref.current.querySelector<HTMLElement>('[data-autofocus], input, select, textarea, button:not([data-nav-close]), a[href]');
      if (first) first.focus({ preventScroll: true });
      else if (getInput() !== 'mouse') focusFirst();
    }, 20);
    return () => {
      clearTimeout(t);
      audio.play('menuClose');
      if (prev?.isConnected && prev !== document.body) prev.focus({ preventScroll: true });
      else if (getInput() === 'gamepad' || getInput() === 'keyboard') setTimeout(focusFirst, 0);
    };
  }, [ref]);
}

// ---------- Modal (a navigation scope; B / Esc closes it) ----------
export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayerFocus(ref);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={ref} data-nav-scope>
        <div className="row between">
          <h2>{title}</h2>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label="Cerrar" data-nav-close>
            <Icon name="x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Avatar({ name, url, size }: { name: string; url?: string | null; size?: 'lg' }) {
  return <span className={`avatar${size ? ' ' + size : ''}`}>{url ? <img src={url} alt="" /> : initials(name)}</span>;
}

// ---------- Confirm (gamepad-friendly replacement for window.confirm) ----------
type ConfirmOpts = { title?: string; confirmLabel?: string; danger?: boolean };
type ConfirmFn = (message: string, opts?: ConfirmOpts) => Promise<boolean>;
const ConfirmCtx = createContext<ConfirmFn>(async (m) => window.confirm(m));

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [req, setReq] = useState<{ message: string; opts: ConfirmOpts; resolve: (v: boolean) => void } | null>(null);
  const confirm = useCallback<ConfirmFn>((message, opts = {}) => new Promise((resolve) => setReq({ message, opts, resolve })), []);
  const done = (v: boolean) => {
    req?.resolve(v);
    setReq(null);
  };
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {req && (
        <Modal title={req.opts.title ?? '¿Seguro?'} onClose={() => done(false)}>
          <p className="muted">{req.message}</p>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => done(false)}>
              Cancelar
            </button>
            <button className={`btn ${req.opts.danger ? 'btn-danger' : 'btn-primary'}`} onClick={() => done(true)} data-autofocus>
              {req.opts.confirmLabel ?? 'Confirmar'}
            </button>
          </div>
        </Modal>
      )}
    </ConfirmCtx.Provider>
  );
}

/** `const confirm = useConfirm(); if (await confirm('¿Borrar…?', { danger: true })) …` */
export function useConfirm() {
  return useContext(ConfirmCtx);
}
